import React, { useEffect, useState, Suspense } from 'react';
import Navigation from '../components/Navigation';
import Hero from '../components/Hero';
import About from '../components/About';

// Lazy load components
const Projects = React.lazy(() => import('../components/Projects'));
const Process = React.lazy(() => import('../components/Process'));
const Skills = React.lazy(() => import('../components/Skills'));
const Testimonials = React.lazy(() => import('../components/Testimonials'));
const FAQ = React.lazy(() => import('../components/FAQ'));
const WorldMap = React.lazy(() => import('../components/WorldMap'));
const Contact = React.lazy(() => import('../components/Contact'));
const Footer = React.lazy(() => import('../components/Footer'));

function Home() {
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Update page title
    document.title = 'Mespi.dev - Profesjonalne rozwiązania webowe';
  }, []);

  useEffect(() => {
    // Cursor glow effect - critical
    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Defer non-critical setup
    setTimeout(() => {
      // Intersection Observer for animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      }, observerOptions);

      document.querySelectorAll('.project-card, .skill-item, .testimonial-card').forEach(el => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
        el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observer.observe(el);
      });

      // Console ASCII art
      console.log(`
%c
 ███╗   ███╗███████╗███████╗██████╗ ██╗   ██████╗ ███████╗██╗   ██╗
 ████╗ ████║██╔════╝██╔════╝██╔══██╗██║   ██╔══██╗██╔════╝██║   ██║
 ██╔████╔██║█████╗  ███████╗██████╔╝██║   ██║  ██║█████╗  ██║   ██║
 ██║╚██╔╝██║██╔══╝  ╚════██║██╔═══╝ ██║   ██║  ██║██╔══╝  ╚██╗ ██╔╝
 ██║ ╚═╝ ██║███████╗███████║██║     ██║██╗██████╔╝███████╗ ╚████╔╝ 
 ╚═╝     ╚═╝╚══════╝╚══════╝╚═╝     ╚═╝╚═╝╚═════╝ ╚══════╝  ╚═══╝  
%c
👋 Witaj na Mespi.dev!
Tworzymy nowoczesne rozwiązania webowe z pasją do szczegółów.
`, 'color: #01FF00; font-weight: bold;', 'color: #8b949e;');

      // Secret code easter egg
      let secretCode = ['s', 'e', 'c', 'r', 'e', 't'];
      let secretIndex = 0;
      let lastKeyTime = Date.now();

      const handleKeyDown = (e) => {
        const currentTime = Date.now();
        if (currentTime - lastKeyTime > 2000) {
          secretIndex = 0;
        }
        lastKeyTime = currentTime;

        if (e.key.toLowerCase() === secretCode[secretIndex]) {
          secretIndex++;
          if (secretIndex === secretCode.length) {
            activateMatrixRain();
            secretIndex = 0;
          }
        } else {
          secretIndex = 0;
        }
      };

      const activateMatrixRain = () => {
        const matrixDiv = document.createElement('div');
        matrixDiv.className = 'matrix-rain';
        document.body.appendChild(matrixDiv);

        setTimeout(() => matrixDiv.classList.add('active'), 10);

        const chars = '010101010101010101010101010101010101010101010101010101010101010101アイウエオカキクケコサシスセソタチツテト';
        const columns = Math.min(Math.floor(window.innerWidth / 20), 50); // Limit to 50 columns to avoid too many tasks

        for (let i = 0; i < columns; i++) {
          setTimeout(() => {
            const char = document.createElement('div');
            char.className = 'matrix-char';
            char.textContent = chars[Math.floor(Math.random() * chars.length)];
            char.style.left = (i * 20) + 'px';
            char.style.top = '-20px';
            char.style.animationDuration = (Math.random() * 3 + 2) + 's';
            matrixDiv.appendChild(char);
          }, i * 10);
        }

        setTimeout(() => {
          matrixDiv.classList.remove('active');
          setTimeout(() => matrixDiv.remove(), 500);
        }, 5000);
      };

      document.addEventListener('keydown', handleKeyDown);

      // Scroll to bottom secret box
      let secretBoxShown = false;
      const handleScroll = () => {
        if (secretBoxShown) return;
        
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const clientHeight = window.innerHeight;
        
        if (scrollTop + clientHeight >= scrollHeight - 50) {
          secretBoxShown = true;
          showSecretBox();
        }
      };

      const showSecretBox = () => {
        const box = document.createElement('div');
        box.className = 'secret-box';
        box.innerHTML = `
          <h4>I to by było na tyle...</h4>
        `;
        document.body.appendChild(box);
        
        setTimeout(() => box.classList.add('show'), 100);
        
        setTimeout(() => {
          if (box.parentElement) {
            box.classList.remove('show');
            setTimeout(() => box.remove(), 500);
          }
        }, 5000);
      };

      window.addEventListener('scroll', handleScroll);
    }, 0);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      // Note: Other listeners are cleaned up in the setTimeout, but for simplicity, assuming component unmounts rarely
    };
  }, []);

  return (
    <div className="App">
      <div 
        className="cursor-glow" 
        style={{ left: `${cursorPos.x}px`, top: `${cursorPos.y}px` }}
      />
      <Navigation />
      <Hero />
      <About />
      <Suspense fallback={<div>Loading...</div>}>
        <Projects />
        <Process />
        <Skills />
        <Testimonials />
        <FAQ />
        <WorldMap />
        <Contact />
        <Footer />
      </Suspense>
    </div>
  );
}

export default Home;
